import React from "react";
import styles from "./user-credentials-modal.module.scss";
import Backdrop from "../backdrop";
import { CenterCenterContainer } from "@src/components/templates/layouts/center-center-container/center-center-container";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import closeIcon from "@src/assets/svg/close-icon.svg";
import copyIcon from "@src/components/organisms/node-details-panel/copy-node-icon.svg";
import { WarningGlassyModalHeader } from "@src/components/molecules/warning-glassy-modal-header/warning-glassy-modal-header";
import { successSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import { UserCredentials } from "@src/types/user-management";
import { InfomationDisplayBlock } from "@src/components/organisms/information-display-block/information-display-block";

const cssClassName = "user-credentials-modal";

interface UserCredentialsModalProps extends UserCredentials {
  onClose: () => void;
}

export const UserCredentialsModal = ({
  id,
  key_id,
  private_key,
  expiration_date,
  onClose,
}: UserCredentialsModalProps) => {
  const i18n = useI18N();

  const texts = {
    title: i18n("modules.userCredentialsModal.title"),
    warningText: i18n("modules.userCredentialsModal.warningText"),
    userIdLabel: i18n("modules.userCredentialsModal.userIdLabel"),
    keyIdLabel: i18n("modules.userCredentialsModal.keyIdLabel"),
    privateKeyLabel: i18n("modules.userCredentialsModal.privateKeyLabel"),
    expirationDateLabel: i18n(
      "modules.userCredentialsModal.expirationDateLabel",
    ),
    downloadButton: i18n("modules.userCredentialsModal.downloadButton"),
    closeButton: i18n("modules.userCredentialsModal.closeButton"),
    copySuccessText: i18n("modules.userCredentialsModal.copySuccessText"),
    copyTooltip: i18n("modules.userCredentialsModal.copyTooltip"),
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      successSnackbar(texts.copySuccessText);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownload = () => {
    try {
      const content = JSON.stringify(
        {
          user_id: id,
          key_id: key_id,
          private_key: private_key,
          expiration_date: expiration_date,
        },
        null,
        2,
      );
      const blob = new Blob([content], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "user-credentials.json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download credentials:", err);
    }
  };

  return (
    <div className={styles[cssClassName]}>
      <Backdrop />
      <CenterCenterContainer>
        <div className={styles[`${cssClassName}__content`]}>
          <WarningGlassyModalHeader
            title={texts.title}
            warningText={texts.warningText}
            rightIcon={closeIcon}
            rightIconFunction={onClose}
          />

          <div className={styles[`${cssClassName}__body`]}>
            <FlexColumnContainer verticalGap={24}>
              <div className={styles[`${cssClassName}__fields`]}>
                <InfomationDisplayBlock
                  id={id}
                  label={texts.userIdLabel}
                  tooltip={id}
                  tooltipPosition="top"
                  copyFunction={() => copyToClipboard(id)}
                  copyTooltip={texts.copyTooltip}
                  copyTooltipPosition="left"
                />

                <InfomationDisplayBlock
                  id={key_id}
                  label={texts.keyIdLabel}
                  tooltip={key_id}
                  tooltipPosition="top"
                  copyFunction={() => copyToClipboard(key_id)}
                  copyTooltip={texts.copyTooltip}
                  copyTooltipPosition="left"
                />

                <div className={styles[`${cssClassName}__private-key-field`]}>
                  <div
                    className={styles[`${cssClassName}__private-key-header`]}
                  >
                    <span
                      className={styles[`${cssClassName}__private-key-label`]}
                    >
                      {texts.privateKeyLabel}
                    </span>
                    <IconButton
                      fun={() => copyToClipboard(private_key)}
                      icon={copyIcon}
                      tooltip={texts.copyTooltip}
                      tooltipPosition="left"
                    />
                  </div>
                  <input
                    type="password"
                    value={private_key}
                    readOnly
                    className={styles[`${cssClassName}__private-key-input`]}
                  />
                </div>

                <InfomationDisplayBlock
                  id={expiration_date}
                  label={texts.expirationDateLabel}
                  tooltip={expiration_date}
                  tooltipPosition="top"
                  copyFunction={() => copyToClipboard(expiration_date)}
                  copyTooltip={texts.copyTooltip}
                  copyTooltipPosition="left"
                />
              </div>

              <FlexRowContainer justifyContent="center">
                <Button
                  fun={handleDownload}
                  text={texts.downloadButton}
                  variant={["full-width"]}
                />
                <Button
                  fun={onClose}
                  text={texts.closeButton}
                  variant={["secondary", "full-width"]}
                />
              </FlexRowContainer>
            </FlexColumnContainer>
          </div>
        </div>
      </CenterCenterContainer>
    </div>
  );
};
